<template>
  <div class="barinfo">
    <el-row
      align="middle"
      class="barinfo__content"
      :class="{ nomanagement: !userHasManagementPermission }"
      justify="space-between"
      type="flex"
    >
      <PopoverInfoReparto v-if="userHasManagementPermission" />

      <div v-if="device !== 'mobile' && userHasManagementPermission" class="barinfo__options">
        <DrawerActions />
        <el-button class="share-button notification p-0" type="text" @click="handleRelocate">
          <el-badge class="item" type="info" value="F9">
            <i class="material-icons large">my_location</i>
            <label class="label-menu-item" style="font-size: 8px">Reubicar</label>
          </el-badge>
        </el-button>
        <Stats class="mx-2" />
        <Screenfull />
      </div>

      <div
        v-if="userHasManagementPermission"
        :class="{ 'has-notifications': notificationsAndNotDeliveredCount }"
        class="barinfo__notifications ml-2"
        @click="showNotificationsDrawer"
      >
        <div class="barinfo__notifications__button">
          <div
            v-if="notificationsAndNotDeliveredCount > 0"
            class="barinfo__notifications__button__number"
          >
            <span>{{ notificationsAndNotDeliveredCount }}</span>
          </div>
          <div class="barinfo__notifications__button__icon material-icons">error</div>
          <div class="barinfo__notifications__button__label">Notificaciones</div>
        </div>
      </div>

      <div class="spacer"></div>

      <WorkgroupCentralSelector
        v-if="userHasManagementPermission"
        :value="selectedWorkgroupsAndCentrals"
        allow-select-multiple-centrals
        allow-select-multiple-workgroups
        class="mx-2"
        :class="{ 'ml-auto': !userHasManagementPermission }"
        @input="handleChangeSelectedWorkgroups"
      />

      <PopoverMuteSound v-if="userHasManagementPermission" class="mx-1" />

      <BreakTime />

      <el-dropdown trigger="click" @command="onCommandMenu">
        <el-avatar icon="el-icon-user-solid" shape="square" size="medium" />
        <el-dropdown-menu slot="dropdown">
          <el-dropdown-item disabled> Perfil Usuario </el-dropdown-item>
          <el-dropdown-item v-if="userHasManagementPermission" command="setupPhoneExtension">
            Configurar extensión teléfono
          </el-dropdown-item>
          <el-dropdown-item command="logout"> Cerrar sesión </el-dropdown-item>
          <el-dropdown-item disabled divided>
            Hora:
            <span class="font-weight-bold">{{ time }}</span>
          </el-dropdown-item>
          <el-dropdown-item disabled divided>
            Versión:
            <span class="font-weight-bold">{{ buildInfo.version }}</span>
          </el-dropdown-item>
          <el-dropdown-item disabled>
            Build: <span class="font-weight-bold">{{ buildInfo.date }}</span>
          </el-dropdown-item>
          <el-dropdown-item disabled>
            Rol:
            {{
              $store.state.operators.roles.length
                ? $store.state.operators.roles[0]
                : "superadministrador"
            }}
          </el-dropdown-item>

          <el-dropdown-item v-if="isTestingEnvironment" disabled divided>
            <strong>Versión de pruebas</strong>
          </el-dropdown-item>
          <el-dropdown-item disabled>
            Si detectas alguna inestabilidad puedes ir a la versión de producción
          </el-dropdown-item>
          <el-dropdown-item command="production"> Versión de producción </el-dropdown-item>
        </el-dropdown-menu>
      </el-dropdown>
    </el-row>
  </div>
</template>

<script>
import Screenfull from "@/components/Screenfull/index.vue";
import Stats from "@/components/Stats/index.vue";
import WorkgroupCentralSelector from "@/components/common/forms/WorkgroupCentralSelector.vue";
import DrawerActions from "@/components/navbar/DrawerActions.vue";
import PopoverInfoReparto from "@/components/navbar/PopoverInfoReparto.vue";
import PopoverMuteSound from "@/components/navbar/PopoverMuteSound.vue";
import flavours from "@/mixins/flavours.js";
import BreakTime from "@/modules/breaktime/components/BreakTime.vue";
import moment from "@/utils/moment";
import { BUILD_TIMESTAMP, VERSION } from "@/utils/version.js";
import { mapActions, mapGetters } from "vuex";

export default {
  name: "BarInfo",

  components: {
    WorkgroupCentralSelector,
    Screenfull,
    Stats,
    PopoverMuteSound,
    PopoverInfoReparto,
    DrawerActions,
    BreakTime,
  },

  mixins: [flavours],

  data() {
    return {
      audio: null,
      dialogVoiceVisible: false,
      notificationVoice: undefined,
      dialogAlarmVisible: false,
      notificationAlarm: undefined,
      notificationsIdShowed: [],
      timeoutShowNotifications: undefined,
      notificationsToShow: [],
    };
  },

  created() {
    this.audio = new Audio("https://2kbo.com/notification.mp3");
  },

  computed: {
    ...mapGetters("pbxCalls", ["calls", "endpoints"]),
    ...mapGetters(["device"]),
    ...mapGetters("clientNotifications", ["notifications"]),
    ...mapGetters("clientMuteSoundPopover", ["muteSoundOptions"]),
    ...mapGetters("operators", ["userHasManagementPermission"]),
    ...mapGetters("app", ["selectedWorkgroupsAndCentrals"]),
    ...mapGetters("services", ["servicesNotDelivered", "bookingsNotDelivered"]),

    isTestingEnvironment() {
      return window.location.hostname.match(/(testing|local|127.0.0.1)/) !== null;
    },

    notificationsAndNotDeliveredCount() {
      return (
        this.notifications.length +
        Object.values(this.servicesNotDelivered).length +
        Object.values(this.bookingsNotDelivered).length
      );
    },

    buildInfo() {
      return {
        version: VERSION,
        date: moment()(BUILD_TIMESTAMP).format("DD/MM/YYYY HH:mm"),
      };
    },

    time() {
      return moment()().format("HH:mm");
    },
  },

  watch: {
    notificationsAndNotDeliveredCount(count) {
      if (this.muteSoundOptions.muteSoundAll) {
        if (this.audio) {
          this.audio.pause();
        }
      }
      if (count > 0 && this.calls.length === 0) {
        if (
          (!this.audio || this.audio.paused) &&
          !this.muteSoundOptions.muteSoundAll &&
          this.muteSoundOptions.muteSoundNotifications
        ) {
          this.audio.volume = 1;
          if (this.audio) {
            this.audio.play();
          }
        }
      } else {
        if (this.audio) {
          this.audio.pause();
        }
      }
    },
  },

  methods: {
    handleRelocate() {
      this.$store.dispatch("reubicarExpulsar/showDialog", {
        show: true,
      });
    },

    ...mapActions("ui", ["showNotificationsDrawer"]),
    ...mapActions("operators", ["logout"]),
    ...mapActions("clientNotifications", ["answerNotification"]),
    ...mapActions("app", ["setSelectedWorkgroupsAndCentrals", "setShowExtensionSetupModal"]),

    handleChangeSelectedWorkgroups(value) {
      this.setSelectedWorkgroupsAndCentrals(value);
    },

    handleSetupPhoneExtension() {
      this.setShowExtensionSetupModal(true);
    },

    onCommandMenu(command) {
      switch (command) {
        case "logout":
          this.logoutOperator();
          break;
        case "setupPhoneExtension":
          this.handleSetupPhoneExtension();
          break;
        case "production":
          let productionUrl = "https://central.2kbo.com";
          if (this.isNeositek) {
            productionUrl = "https://central.neositek.com";
          }
          location.href = productionUrl;
          break;

        default:
          break;
      }
    },

    async logoutOperator() {
      const shouldRedirect = await this.logout();
      if (shouldRedirect) {
        this.$router.push({ name: "login" });
      }
    },
  },

  beforeDestroy() {
    clearTimeout(this.timeoutShowNotifications);
    if (this.audio) {
      this.audio.pause();
      this.audio.src = null;
      this.audio = null;
    }
  },
};
</script>

<style lang="scss" scoped>
.barinfo {
  height: 50px;

  &__content {
    height: 50px;
    display: grid;
    grid-template-columns:
      170px minmax(140px, 1fr) 100px minmax(10px, 140px) minmax(200px, 1fr) 55px
      40px 40px;

    &.nomanagement {
      display: flex;
      justify-content: flex-end;
    }
  }

  &__options,
  &__notifications {
    height: 50px;
    display: flex;
    justify-content: center;
    align-items: center;

    &__button {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      cursor: pointer;
      position: relative;

      &__icon {
        font-size: 24px;
        color: white;
        margin-bottom: 2px;
      }

      &__label {
        color: white;
        font-size: 8px;
        font-weight: bold;
      }

      &__number {
        position: absolute;
        top: -5px;
        right: 6px;
        font-size: 10px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: bold;
        background-color: #f44;
        min-width: 18px;
        height: 18px;
        border-radius: 9px;
        color: white;
      }
    }
  }

  i {
    display: block;
    font-size: 200%;
  }

  .el-avatar {
    cursor: pointer;
  }
}

.has-notifications {
  background-color: $menuNotifications;
  border-radius: 5px;
  animation: pulse2 1s infinite;
}

@keyframes pulse2 {
  0% {
    background-color: #304156;
  }

  100% {
    background-color: $menuNotifications;
  }
}

::v-deep .el-badge__content {
  font-size: 8px;
  font-weight: bold;
  padding: 0 6px;
}
</style>
